import React, { useMemo } from 'react';

import { ModifierListItem } from 'features/menu/components/product-detail/modifier/modifier-list-item';
import {
  getSelectedMultiplier,
  isMultiplierVisuallySelected,
} from 'features/menu/components/product-detail/modifier/utils';
import { StyleSheet, View } from 'react-native';

import { ListCardGroup } from 'components/list-card-group';
import { getCatalogIncludedModifiers } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-catalog-preview/get-catalog-included';
import { t } from 'state/intl/translations';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { spacing } from 'styles/constants';

type CatalogIncludedSectionProps = {
  menu: IndexedMenu<ILocalizedMenuEntry>;
  product: ILocalizedMenuEntry;
};

const NOOP = () => undefined;

/**
 * Read-only “Included” modifiers for store-less item/picker deeplink preview.
 * Combos use CatalogComboSlotsSection instead (all slot product options).
 */
export function CatalogIncludedSection({ menu, product }: CatalogIncludedSectionProps) {
  const includedModifiers = useMemo(() => getCatalogIncludedModifiers(menu, product), [
    menu,
    product,
  ]);

  if (includedModifiers.length === 0) {
    return null;
  }

  return (
    <View style={styles.container} testID="menu-catalog-included">
      {/* Disable the whole group so we don't wrap each card (ProductCardModifier
          uses width 33.3% of its parent — a per-card wrapper collapses that). */}
      <View pointerEvents="none" style={styles.modifiersGroup}>
        <ListCardGroup
          header={t('included')}
          items={includedModifiers}
          renderItem={({ item }) => {
            const selectedMultiplier = getSelectedMultiplier(item.modifier, item.selections);
            if (!selectedMultiplier) {
              return null;
            }
            return (
              <ModifierListItem
                key={item.modifier.id}
                content={item.modifier}
                // No store selected — prices are store-specific; keep calories only.
                itemValue={{ ...selectedMultiplier, price: undefined }}
                isSelected={
                  item.modifier.disabled || isMultiplierVisuallySelected(selectedMultiplier)
                }
                onCardPress={NOOP}
              />
            );
          }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: spacing.$2,
    width: '100%',
  },
  modifiersGroup: {
    width: '100%',
  },
});
